import type { i18n as I18nInstance } from 'i18next';
import type { QuestionConfigBranchLoaderProps, QuestionConfigQuestionType } from '/index';

import initiativeCarCardBackground from '/business/shared/images/initiatives/Business-travel-1x.jpg';
import initiativeCarBackground from '/business/shared/images/initiatives/Business-travel-2x.jpg';
import initiativeRailCardBackground from '/business/shared/images/initiatives/Rail-card-image-1x.jpg';
import initiativeRailBackground from '/business/shared/images/initiatives/Rail-header.png';
import initiativeTaxiCardBackground from '/business/shared/images/initiatives/Taxi-card-image-1x.jpg';
import initiativeTaxiBackground from '/business/shared/images/initiatives/Taxi-header.png';
import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';
import formatNumber from '/shared/functions/formatNumber';
import vehicleIcons from '/shared/util/vehicleIcons';

import { getConfigureCastrolAirSingular } from './getConfigureCastrolAirSingular';
import useFormatSharedTravelCarTaxiSupplier from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatSharedTravelCarTaxiSupplier';
import useFormatSharedTravelRailAirSupplier from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatSharedTravelRailAirSupplier';
import { bottomContentsSupplier } from '/business/shared/components/questions/scope3/_education/bottomScope3';
import educationContentJson from '/business/shared/components/questions/scope3/businessTravel/_education/businessTravel.json';
import useGetTaxiSchema from '/business/shared/components/questions/scope3/businessTravel/_hooks/useGetTaxiSchema';
import useGetRailSchema from '/business/shared/components/questions/scope3/businessTravel/_hooks/useGetRailSchema';

import useGetEstimatedCarSchema from '/business/shared/components/questions/scope3/businessTravel/car/_hooks/useGetEstimatedCarSchema';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import { defaultBottomAll } from '/business/shared/components/questions/scope3/_education/bottomBusinessTravel';
import DistanceCanonicalName from '/business/shared/components/widgets/misc/DistanceCanonicalName';

type ConfigureBusinessTravelOptions = {
  i18n: I18nInstance;
  hasScopeBasedOrganisation?: boolean;
  reductionCardsSlides?: string[];
};

type TravelDistanceTableData = {
  distanceTravelled?: number | string | null;
  distanceTravelledUnit: string;
  tonnage?: number | null;
};

type TonnageTableData = {
  tonnage?: number | null;
};

type InitiativeVisualProps = Parameters<
  NonNullable<NonNullable<QuestionConfigQuestionType['initiative']>['getVisuals']>
>[0];

export const getQuestionBusinessTravel = ({
  i18n,
  hasScopeBasedOrganisation = true,
  reductionCardsSlides = [],
}: ConfigureBusinessTravelOptions): QuestionConfigQuestionType => {
  const questionEnums = {
    TRAVEL_CAR: {
      id: 'by-car',
      breadcrumbTitle: 'By car',
      apiKey: 'calculation-travelbycar-estimate',
      title: 'Business travel by car',
      useHookLoader: useGetEstimatedCarSchema,
      scopeCategory: questionScopeCategories.SCOPE_3,
      table: {
        title: 'Calculated carbon emissions for activity data:',
        totalType: basketTableTotalTypes.TONNAGE,
        headings: ['Estimated distance travelled', 'Carbon emissions'],
        cells: [
          ({ distanceTravelled, distanceTravelledUnit }: TravelDistanceTableData) => {
            if (!distanceTravelled) {
              return '-';
            }
            return <DistanceCanonicalName distance={distanceTravelled} unit={distanceTravelledUnit} type="annual" />;
          },
          ({ tonnage }: TonnageTableData) => `${formatNumber(tonnage)} tCO\u2082e`,
        ],
        isAnswerEditable: false,
        tabName: 'Activity data',
        mainQuestionTitle: 'Calculated carbon emissions for business travel by car: ',
        supplier: {
          columns: ['emissionSource', 'vehicleSize', 'supplierName', 'estimateDistance', 'carbonEmissions'],
        },
      },
      initiative: {
        getVisuals: ({ isBpOffering }: InitiativeVisualProps) => {
          if (!isBpOffering) {
            return {
              description: false,
              background: initiativeCarBackground,
              useMask: false,
              cardBackground: initiativeCarCardBackground,
            };
          }
          if (isBpOffering) {
            return {
              description: false,
              background: null,
              useMask: false,
              cardBackground: null,
            };
          }
        },
        formatForInitiativeSummary: () => {
          return {
            isShowingReportingPeriod: true,
            isShowingReportingPeriodNoReduction: false,
            category: false,
            description: 'Distance travelled in km during the reporting period ',
            descriptionNoReduction: false,
          };
        },
      },
      selector: {
        injectQuestionBranchSelector: true,
        questionBranchEnableSupplierData: true,
        para: 'To calculate emissions associated with any car travel by employees for business purposes, you can use a combination of activity data and supplier data.',
        options: [
          {
            title: questionSelectorOptions.ACTIVITY,
            requiredData: 'Estimated distance traveled by car for all employees.',
          },
          {
            title: questionSelectorOptions.SUPPLIER,
            requiredData:
              'Fuel type, vehicle type, supplier name, supplier intensity data/absolute emissions, estimated distance traveled, number of vehicles of the selected size.',
          },
        ],
      },
      education: {
        hasEducationWidget: true,
        educationContent: educationContentJson,
        bottomContentsSupplier: bottomContentsSupplier,
      },
      useQuestionBranchSupplierLoader: (props: QuestionConfigBranchLoaderProps) =>
        useFormatSharedTravelCarTaxiSupplier({
          ...props,
          questionFormatName: 'by-car',
        }),
      supplierAndCustomSubheader: (
        <>
          <StyledParagraph $largeText $hasExtraMarginBottom>
            Enter the details of car journeys undertaken by employees for business purposes during the reporting period:{' '}
            <ReportingPeriod $green />
          </StyledParagraph>
          <StyledInfoFlash>
            <div>For more information, refer to the "Help" section for detailed guidance.</div>
          </StyledInfoFlash>
        </>
      ),
      activitySubheader: (
        <>
          <StyledParagraph $largeText $hasExtraMarginBottom>
            Enter the details of car journeys undertaken by employees for business purposes during the reporting period:{' '}
            <ReportingPeriod $green />
          </StyledParagraph>
          <StyledInfoFlash>
            <div>For more information, refer to the "Help" section for detailed guidance.</div>
          </StyledInfoFlash>
        </>
      ),
    },
    TRAVEL_TAXI: {
      id: 'by-taxi',
      title: 'Business travel by taxi',
      initiative: {
        getVisuals: ({ isBpOffering }: InitiativeVisualProps) => {
          if (!isBpOffering) {
            return {
              description: false,
              background: initiativeTaxiBackground,
              useMask: false,
              cardBackground: initiativeTaxiCardBackground,
            };
          }
          if (isBpOffering) {
            return {
              description: false,
              background: initiativeTaxiBackground,
              useMask: false,
              cardBackground: initiativeTaxiCardBackground,
            };
          }
        },
        formatForInitiativeSummary: () => {
          return {
            isShowingReportingPeriod: true,
            isShowingReportingPeriodNoReduction: false,
            category: false,
            description: 'Distance travelled in km during the reporting period ',
            descriptionNoReduction: false,
          };
        },
      },
      apiKey: 'calculation-travelbytaxi',
      breadcrumbTitle: 'By taxi',
      useHookLoader: useGetTaxiSchema,
      scopeCategory: questionScopeCategories.SCOPE_3,
      details: {},
      table: {
        title: 'Calculated carbon emissions for activity data:',
        totalType: basketTableTotalTypes.TONNAGE,
        headings: ['Estimated distance travelled', 'Carbon emissions'],
        cells: ['distanceTravelled', ({ tonnage }: TonnageTableData) => `${formatNumber(tonnage)} tCO\u2082e`],
        isAnswerEditable: false,
        tabName: 'Activity data',
        mainQuestionTitle: 'Calculated carbon emissions for business travel by taxi:: ',
        supplier: {
          columns: ['emissionSource', 'vehicleSize', 'supplierName', 'estimateDistance', 'carbonEmissions'],
        },
      },
      selector: {
        injectQuestionBranchSelector: true,
        questionBranchEnableSupplierData: true,
        para: 'To calculate emissions associated with any taxi travel by employees for business purposes, you can use a combination of activity data, and custom data.',
        options: [
          {
            title: questionSelectorOptions.ACTIVITY,
            requiredData: 'Estimated distance traveled by taxi for all employees, number of trips.',
          },
          {
            title: questionSelectorOptions.SUPPLIER,
            requiredData:
              'Fuel type, vehicle type, supplier name, supplier intensity data/absolute emissions, estimated distance traveled, number of vehicles of the selected size.',
          },
        ],
      },
      education: {
        hasEducationWidget: true,
        educationContent: educationContentJson,
        bottomContentsSupplier: bottomContentsSupplier,
      },
      useQuestionBranchSupplierLoader: (props: QuestionConfigBranchLoaderProps) =>
        useFormatSharedTravelCarTaxiSupplier({
          ...props,
          questionFormatName: 'by-taxi',
        }),
      supplierAndCustomSubheader: (
        <>
          <StyledParagraph $largeText $hasExtraMarginBottom>
            Enter the details of taxi journeys undertaken by employees for business purposes during the reporting
            period: <ReportingPeriod $green />
          </StyledParagraph>
          <StyledInfoFlash>
            <div>For more information, refer to the "Help" section for detailed guidance.</div>
          </StyledInfoFlash>
        </>
      ),
      activitySubheader: (
        <>
          <StyledParagraph $largeText $hasExtraMarginBottom>
            Enter the details of taxi journeys undertaken by employees for business purposes during the reporting
            period: <ReportingPeriod $green />
          </StyledParagraph>
          <StyledInfoFlash>
            <div>For more information, refer to the "Help" section for detailed guidance.</div>
          </StyledInfoFlash>
        </>
      ),
    },
    TRAVEL_RAIL: {
      id: 'by-rail',
      breadcrumbTitle: 'By rail',
      title: 'Business travel by rail',
      apiKey: 'calculation-travelbyrail',
      useHookLoader: useGetRailSchema,
      scopeCategory: questionScopeCategories.SCOPE_3,
      table: {
        title: 'Calculated carbon emissions for activity data:',
        totalType: basketTableTotalTypes.TONNAGE,
        headings: ['Estimated distance travelled', 'Carbon emissions'],
        cells: ['distanceTravelled', ({ tonnage }: TonnageTableData) => `${formatNumber(tonnage)} tCO\u2082e`],
        isAnswerEditable: false,
        tabName: 'Activity data',
        mainQuestionTitle: 'Calculated carbon emissions for business travel by rail: ',
        supplier: {
          columns: ['estimateDistance', 'carbonEmissions'],
        },
      },
      initiative: {
        getVisuals: ({ isBpOffering }: InitiativeVisualProps) => {
          if (!isBpOffering) {
            return {
              description: false,
              background: initiativeRailBackground,
              useMask: false,
              cardBackground: initiativeRailCardBackground,
            };
          }
          if (isBpOffering) {
            return {
              description: false,
              background: initiativeRailBackground,
              useMask: false,
              cardBackground: initiativeRailCardBackground,
            };
          }
        },
        formatForInitiativeSummary: () => {
          return {
            isShowingReportingPeriod: true,
            isShowingReportingPeriodNoReduction: false,
            category: false,
            description: 'Distance travelled in km during the reporting period ',
            descriptionNoReduction: false,
          };
        },
      },
      details: {},
      selector: {
        injectQuestionBranchSelector: true,
        questionBranchEnableSupplierData: true,
        para: 'To calculate emissions associated with any rail travel by employees for business purposes, you can use a combination of activity data, supplier data, and custom data.',
        options: [
          {
            title: questionSelectorOptions.ACTIVITY,
            requiredData: 'Station information, journey type, number of trips made.',
          },
          {
            title: questionSelectorOptions.SUPPLIER,
            requiredData: 'Emission source, supplier name, absolute emissions.',
          },
        ],
      },
      education: {
        hasEducationWidget: true,
        educationContent: educationContentJson,
        bottomContentsSupplier: bottomContentsSupplier,
      },
      useQuestionBranchSupplierLoader: (props: QuestionConfigBranchLoaderProps) =>
        useFormatSharedTravelRailAirSupplier({
          ...props,
          questionFormatName: 'by-rail',
        }),
      supplierAndCustomSubheader: (
        <>
          <StyledParagraph $largeText $hasExtraMarginBottom>
            Enter the details of rail journeys undertaken by employees for business purposes during the reporting
            period: <ReportingPeriod $green />
          </StyledParagraph>
          <StyledInfoFlash>
            <div>For more information, refer to the "Help" section for detailed guidance.</div>
          </StyledInfoFlash>
        </>
      ),
    },
    TRAVEL_AIR: {
      ...getConfigureCastrolAirSingular({
        i18n,
        hasScopeBasedOrganisation,
      }),
      scopeCategory: questionScopeCategories.SCOPE_3,
      breadcrumbTitle: 'By air',
      title: 'Business travel by air',
      selector: {
        injectQuestionBranchSelector: true,
        questionBranchEnableSupplierData: true,
        para: 'To calculate emissions associated with any air travel by employees for business purposes, you can use a combination of activity data, supplier data, and custom data.',
        options: [
          {
            title: questionSelectorOptions.ACTIVITY,
            requiredData: 'Airport information, journey type, travel class, number of trips made.',
          },
          {
            title: questionSelectorOptions.SUPPLIER,
            requiredData: 'Emission source, supplier name, absolute emissions, quantity.',
          },
        ],
      },
      education: {
        hasEducationWidget: true,
        educationContent: educationContentJson,
        bottomContentsSupplier: bottomContentsSupplier,
      },
      useQuestionBranchSupplierLoader: (props: QuestionConfigBranchLoaderProps) =>
        useFormatSharedTravelRailAirSupplier({
          ...props,
          questionFormatName: 'by-air',
        }),
      supplierAndCustomSubheader: (
        <>
          <StyledParagraph $largeText $hasExtraMarginBottom>
            Enter the details of air travel journeys undertaken by employees for business purposes during the reporting
            period: <ReportingPeriod $green />
          </StyledParagraph>
          <StyledInfoFlash>
            <div>For more information, refer to the "Help" section for detailed guidance.</div>
          </StyledInfoFlash>
        </>
      ),
    },
  };

  return {
    id: 'business-travel',
    hasSidebar: false,
    scopeCategory: questionScopeCategories.SCOPE_3,
    title: i18n.t('business:QuestionPage.forms.emissionSelector.businessTravel'),
    icon: vehicleIcons.airPlane,
    isCompletePropName: 'isBusinessTravelComplete',
    form: import('/business/shared/components/questions/scope3/businessTravel/BusinessTravelSelectorForm'),
    primaryI18nKey: 'business:QuestionPage.forms.emissionSelector.businessTravel',
    subTextI18nKey: 'business:QuestionPage.forms.emissionSelector.businessTravelTip',
    overviewPageTitleI18nKey: 'business:QuestionPage.forms.emissionSelector.businessTravel',
    reductionPageKey: 'businessTravel',
    reductionPageCards: reductionCardsSlides,
    subQuestions: [
      questionEnums.TRAVEL_CAR,
      questionEnums.TRAVEL_TAXI,
      questionEnums.TRAVEL_RAIL,
      questionEnums.TRAVEL_AIR,
    ],
    education: {
      hasEducationWidget: true,
      educationContent: educationContentJson,
      bottomContentsSupplier: bottomContentsSupplier,
      bottomContentsAll: defaultBottomAll,
    },
  };
};
